(function () {
  if (customElements.get('ce-collection') !== undefined) {
    return;
  }

  class Collection extends HTMLParsedElement {
    constructor() {
      super();

      this.selectors = {
        filtersForm: '[data-collection-filters-form]',
        filtersRefreshTarget: '[data-collection-filters-refresh-target]',
        productsRefreshTarget: '[data-collection-products-refresh-target]',
        paginationPreviousRefreshTarget: '[data-collection-pagination-previous-refresh-target]',
        paginationRefreshTarget: '[data-collection-pagination-refresh-target]',
        toaster: 'ce-toaster',
      };

      this.elements = null;

      this.sectionId = this.getAttribute('section-id');
      this.paginationType = this.getAttribute('pagination-type');
      this.searchTerms = this.getAttribute('search-terms');

      this.abortController = null;
      this.isLoading = false;
      this.timeoutDebounceApplyFilters = null;

      this._handleSubmitForm = this._handleSubmitForm.bind(this);
    }

    parsedCallback() {
      this.elements = {
        filtersForms: this.querySelectorAll(this.selectors.filtersForm),
        productsRefreshTarget: this.querySelector(this.selectors.productsRefreshTarget),
        paginationPreviousRefreshTarget: this.querySelector(this.selectors.paginationPreviousRefreshTarget),
        paginationRefreshTarget: this.querySelector(this.selectors.paginationRefreshTarget),
        toaster: document.querySelector(this.selectors.toaster),
      };

      for (let filtersForm of this.elements.filtersForms) {
        filtersForm.addEventListener('submit', this._handleSubmitForm);
      }
    }

    disconnectedCallback() {
      for (let filtersForm of this.elements.filtersForms) {
        filtersForm.removeEventListener('submit', this._handleSubmitForm);
      }
    }

    _handleSubmitForm(event) {
      event.preventDefault();
    }

    _showAnimationLoading() {
      const keyframes = [
        { opacity: 1, pointerEvents: 'auto' },
        { opacity: 0.6, pointerEvents: 'none' },
      ];

      const options = {
        duration: 200,
        easing: Themu.animation.easeIn,
        fill: 'both',
      };

      const animation = this.elements.productsRefreshTarget.animate(keyframes, options);
      animation.addEventListener('finish', () => {
        animation.commitStyles();
        animation.cancel();
      });
    }

    _hideAnimationLoading() {
      const keyframes = [
        { opacity: 0.6, pointerEvents: 'none' },
        { opacity: 1, pointerEvents: 'auto' },
      ];

      const options = {
        duration: 150,
        easing: Themu.animation.easeOut,
        fill: 'both',
      };

      const animation = this.elements.productsRefreshTarget.animate(keyframes, options);
      animation.addEventListener('finish', () => {
        animation.commitStyles();
        animation.cancel();
      });
    }

    async _fetchSection(url) {
      let resource = url;
      if (url.indexOf('?') === -1) {
        resource += `?sections=${this.sectionId}`;
      } else {
        resource += `&sections=${this.sectionId}`;
      }

      try {
        this.abortController = new AbortController();

        const response = await fetch(resource, { signal: this.abortController.signal });

        if (!response.ok) {
          throw Error;
        }

        const responseBody = await response.json();

        window.history.replaceState(null, '', url);

        return new DOMParser().parseFromString(responseBody[this.sectionId], 'text/html');
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.elements.toaster.makeToast('danger', Themu.t.collections.messages.error);
        }
      }
    }

    _replaceHtmlFilters(html) {
      const oldRefreshTargets = this.querySelectorAll(this.selectors.filtersRefreshTarget);
      const newRefreshTargets = html.querySelectorAll(this.selectors.filtersRefreshTarget);

      for (let i = 0; i < newRefreshTargets.length; i++) {
        oldRefreshTargets[i].outerHTML = newRefreshTargets[i].outerHTML;
      }
    }

    _replaceHtmlProducts(html) {
      const newRefreshTarget = html.querySelector(this.selectors.productsRefreshTarget);
      this.elements.productsRefreshTarget.innerHTML = newRefreshTarget.innerHTML;
    }

    _prependToHtmlProducts(html) {
      const newRefreshTarget = html.querySelector(this.selectors.productsRefreshTarget);
      this.elements.productsRefreshTarget.insertAdjacentHTML('afterbegin', newRefreshTarget.innerHTML);
    }

    _appendToHtmlProducts(html) {
      const newRefreshTarget = html.querySelector(this.selectors.productsRefreshTarget);
      this.elements.productsRefreshTarget.insertAdjacentHTML('beforeend', newRefreshTarget.innerHTML);
    }

    _replaceHtmlPaginationPrevious(html) {
      const newRefreshTarget = html.querySelector(this.selectors.paginationPreviousRefreshTarget);
      this.elements.paginationPreviousRefreshTarget.innerHTML = newRefreshTarget.innerHTML;
    }

    _replaceHtmlPagination(html) {
      const newRefreshTarget = html.querySelector(this.selectors.paginationRefreshTarget);
      this.elements.paginationRefreshTarget.innerHTML = newRefreshTarget.innerHTML;
    }

    _focusElement(oldActiveElement) {
      if (oldActiveElement === null || oldActiveElement.id === '') {
        return;
      }

      const newActiveElement = document.querySelector(`#${oldActiveElement.id}`);

      if (typeof newActiveElement.focus === 'function') {
        newActiveElement.focus({ preventScroll: true });
      }

      if (typeof newActiveElement.select === 'function') {
        newActiveElement.select();
      }
    }

    _focusFirstProduct() {
      const elementToFocus = this.querySelector('[data-collection-products-focus-target]');
      elementToFocus.focus({ preventScroll: true });

      window.scrollTo({
        left: 0,
        top: this.offsetTop,
        behavior: Themu.prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    _focusProduct(position) {
      const elementToFocus = this.querySelector(`[aria-posinset='${position}'] [data-collection-products-focus-target]`);
      elementToFocus.focus({ preventScroll: true });
    }

    abortFetch() {
      this.abortController?.abort();
      this.abortController = null;

      clearTimeout(this.timeoutDebounceApplyFilters);
    }

    async applyFilters(eventTarget) {
      this.abortFetch();

      this.timeoutDebounceApplyFilters = setTimeout(async () => {
        if (!this.isLoading) {
          this._showAnimationLoading();
        }

        this.isLoading = true;

        const oldActiveElement = document.activeElement;

        const form = eventTarget.closest(this.selectors.filtersForm) || this.querySelector(`#${eventTarget.getAttribute('form')}`);
        const formData = new FormData(form);

        for (let [key, value] of formData.entries()) {
          if (value === '') {
            formData.delete(key);
          }
        }

        let searchParams = new URLSearchParams(formData);

        if (this.searchTerms !== null && this.searchTerms !== '') {
          searchParams.set('q', this.searchTerms);
        }

        const url = `${window.location.pathname}?${searchParams.toString()}`;

        const html = await this._fetchSection(url);

        if (html === undefined) {
          return;
        }

        this._replaceHtmlFilters(html);
        this._replaceHtmlProducts(html);
        this._replaceHtmlPaginationPrevious(html);
        this._replaceHtmlPagination(html);

        this._hideAnimationLoading();
        this.isLoading = false;

        this._focusElement(oldActiveElement);
      }, 250);
    }

    async removeFilter(url) {
      if (!this.isLoading) {
        this._showAnimationLoading();
      }

      this.isLoading = true;

      const oldActiveElement = document.activeElement;

      this.abortFetch();
      const html = await this._fetchSection(url);

      if (html === undefined) {
        return;
      }

      this._replaceHtmlFilters(html);
      this._replaceHtmlProducts(html);
      this._replaceHtmlPaginationPrevious(html);
      this._replaceHtmlPagination(html);

      this._hideAnimationLoading();
      this.isLoading = false;

      this._focusElement(oldActiveElement);
    }

    async paginatePage(url) {
      if (!this.isLoading) {
        this._showAnimationLoading();
      }

      this.isLoading = true;

      this.abortFetch();
      const html = await this._fetchSection(url);

      if (html === undefined) {
        return;
      }

      this._replaceHtmlProducts(html);
      this._replaceHtmlPagination(html);

      this._hideAnimationLoading();
      this.isLoading = false;

      this._focusFirstProduct();
    }

    async paginateLoadPrevious(url, previousPosition) {
      this.abortFetch();
      const html = await this._fetchSection(url);

      if (html === undefined) {
        return;
      }

      this._prependToHtmlProducts(html);
      this._replaceHtmlPaginationPrevious(html);

      this._focusProduct(previousPosition);
    }

    async paginateLoadMore(url, nextPosition) {
      this.abortFetch();
      const html = await this._fetchSection(url);

      if (html === undefined) {
        return;
      }

      this._appendToHtmlProducts(html);
      this._replaceHtmlPagination(html);

      this._focusProduct(nextPosition);
    }

    async paginateInfiniteScroll(url) {
      this.abortFetch();
      const html = await this._fetchSection(url);

      if (html === undefined) {
        return;
      }

      this._appendToHtmlProducts(html);
      this._replaceHtmlPagination(html);
    }
  }

  customElements.define('ce-collection', Collection);
})();
